// Helper for dispatching notification events to Decor::Suite::NotificationManager.
// Stimulus actions on the manager root listen for `<identifier>:show@window`,
// so the dispatched event name must match the rendered manager's identifier —
// the Suite one, not the Daisy fallback.
//
// Types are defined locally; the gem's controller is plain JS and exports no
// type information. Only fields actually used by the wrappers below are typed
// here — extend cautiously to match the gem controller's accepted shape.

import { SafeHTMLContent } from "lib/types";
import { DecorSuiteNotificationManagerIdentifier } from "decor";

export type NotificationVariant =
  | "neutral"
  | "success"
  | "error"
  | "warning"
  | "info";
export type NotificationActionStyle = "primary" | "ghost";
export type NotificationTimeout = number | "sticky";

export interface NotificationDestination {
  text: string;
  href: string;
}

export interface NotificationAction {
  text: string;
  style?: NotificationActionStyle;
  href?: string;
  eventName?: string;
}

export interface NotificationShowOptions {
  variant?: NotificationVariant;
  title?: string;
  body?: string | SafeHTMLContent;
  bodyHref?: string;
  destination?: NotificationDestination;
  actions?: NotificationAction[];
  timeout?: NotificationTimeout;
}

export interface NotificationDismissOptions {
  id: string;
}

export type NotificationShowEvent = CustomEvent<NotificationShowOptions>;
export type NotificationDismissEvent = CustomEvent<NotificationDismissOptions>;

// Re-exported as a local alias so callers below can interpolate it into
// event-name template literals without re-importing.
export const NOTIFICATION_MANAGER_CLASS_NAME =
  DecorSuiteNotificationManagerIdentifier;

export function showNotification(opts: NotificationShowOptions) {
  const evt = new CustomEvent(`${NOTIFICATION_MANAGER_CLASS_NAME}:show`, {
    bubbles: true,
    cancelable: false,
    detail: opts,
  });
  window.dispatchEvent(evt);
}

export function dismissAllNotifications() {
  const evt = new CustomEvent(`${NOTIFICATION_MANAGER_CLASS_NAME}:dismissAll`, {
    bubbles: true,
    cancelable: false,
  });
  window.dispatchEvent(evt);
}

export function dismissSingleNotification(opts: NotificationDismissOptions) {
  const evt = new CustomEvent(`${NOTIFICATION_MANAGER_CLASS_NAME}:dismiss`, {
    bubbles: true,
    cancelable: false,
    detail: opts,
  });
  window.dispatchEvent(evt);
}
